/**
 * Anthra AI — API client
 *
 * The ONLY place the application performs network I/O. `fetch` in a component is
 * an ESLint error and a direct `axios` import is blocked, so everything routes
 * through here (§13 TanStack Query, AGENTS.md §4).
 *
 * BUILT ON `fetch`, NOT AXIOS. The interceptor pattern from the existing axios
 * client carries over — the base URL, the auth attachment and the session
 * handling all live here — but the transport is `fetch` for one reason: this
 * package is shared with the Expo apps, and `fetch` is the only client that behaves
 * identically on both. axios works on React Native via its XHR adapter, but
 * timeouts, upload progress and abort behave differently there, and a shared
 * client whose semantics differ per platform is worse than no shared client.
 *
 * What is added over the original, on both platforms:
 *
 *   - Zod parsing of every response BEFORE it reaches the query cache
 *   - the `snake_case` wire convention, applied once (./wire) rather than spelled
 *     into every schema
 *   - the renewed session token taken off `X-Session-Token`, which is the whole
 *     of the silent half-life exchange (US-00-001.16)
 *   - the request ID bound from the response, joining client errors to server logs
 *
 * NATIVE NOTE: `new URL()` below needs `react-native-url-polyfill/auto`
 * imported at the top of the native entry point. Hermes ships a partial URL
 * whose `searchParams` is incomplete, and the failure is a query string that is
 * silently dropped rather than an error. The Expo template imports it.
 *
 * WHAT THIS GUARANTEES, ONCE, FOR EVERY ENDPOINT
 *   - Every response is parsed by a Zod schema BEFORE it reaches the cache. A
 *     network response is untrusted input, even from our own service.
 *   - Every failure becomes an ApiError with the standard shape and the
 *     correlation ID attached.
 *   - The correlation ID from each response is bound to the logger and the
 *     analytics client, so a client-side error is joinable to the server trace.
 *   - Auth token attachment, renewal and the 401 that ends a session happen in
 *     one place.
 *   - Every request has a timeout. A hung request otherwise spins forever.
 */

import { z } from "zod"

import { analytics } from "../analytics"
import { ApiError, correlationIdFrom, NetworkError, SchemaError } from "../errors"
import { host } from "../host"
import { logger } from "../logger"
import { fromWire, toWire, toWireQuery } from "./wire"

/**
 * Resolved lazily, and accepting both prefixes: Vite requires `VITE_`, Expo
 * requires `EXPO_PUBLIC_`, and shared code has to read whichever the host
 * provides.
 *
 * NOT a module-level const: that would evaluate at import time, before
 * `installHost()` runs in the app entry point.
 */
function baseUrl(): string {
	const h = host()
	return h.env("VITE_API_BASE_URL") ?? h.env("EXPO_PUBLIC_API_BASE_URL") ?? ""
}
const DEFAULT_TIMEOUT_MS = 30_000

export type RequestOptions = {
	method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE"
	body?: unknown
	query?: Record<string, string | number | boolean | undefined | null>
	headers?: Record<string, string>
	signal?: AbortSignal
	timeoutMs?: number
	/**
	 * Required on any non-idempotent operation with a side effect that must not
	 * happen twice — payments, refunds, sends (API_STYLE_GUIDE.md §7). Without it
	 * a network retry becomes a double charge.
	 */
	idempotencyKey?: string
	/** Skip the Authorization header, for login and public endpoints. */
	anonymous?: boolean
}

// -----------------------------------------------------------------------------
// Token handling
//
// Held in memory, never in localStorage — a token in localStorage is readable by
// any XSS payload (SECURITY.md §5). On web there is no token here at all: the
// JWT is an httpOnly, Secure, SameSite=Lax cookie the browser sends with
// `credentials: "include"` and JavaScript never sees (US-00-001.15).
// -----------------------------------------------------------------------------

let accessToken: string | null = null

export function setAccessToken(token: string | null): void {
	accessToken = token
}

export function getAccessToken(): string | null {
	return accessToken
}

/**
 * The header the API renews a session with.
 *
 * THERE IS NO REFRESH ENDPOINT. The service slides the idle window on every
 * authenticated request and, once a token is past half its life, returns a
 * freshly-minted one on the response as `X-Session-Token` (US-00-001.16). A
 * client that POSTed to a refresh route got a 404 and never exchanged anything;
 * reading the header is the exchange.
 */
export const SESSION_TOKEN_HEADER = "X-Session-Token"

/**
 * Notified whenever a response carries a renewed token.
 *
 * The listener — registered by ../session, which is the one module that knows
 * where a token may be kept on each platform — decides whether to persist it.
 * The transport deliberately does not: on web, writing it anywhere would be the
 * violation US-00-001.15 exists to prevent.
 */
let onSessionToken: ((token: string) => void) | undefined

export function setSessionTokenHandler(handler: ((token: string) => void) | undefined): void {
	onSessionToken = handler
}

/** Called when a session is refused, so the app can route to sign-in exactly once. */
let onUnauthenticated: (() => void) | undefined

export function setUnauthenticatedHandler(handler: () => void): void {
	onUnauthenticated = handler
}

// -----------------------------------------------------------------------------
// Core request
// -----------------------------------------------------------------------------

/**
 * SAME ORIGIN IS THE DEFAULT, AND IT HAS TO BE.
 *
 * `baseUrl()` is "" when neither VITE_API_BASE_URL nor EXPO_PUBLIC_API_BASE_URL
 * is set, which is the intended web development setup: vite.config.ts proxies
 * /api so the app and the service share an origin, and the comment there says
 * why — "so the httpOnly session cookie behaves exactly as it does in
 * production".
 *
 * But `new URL("/api/v1/auth/otp")` with no base THROWS. So the configuration
 * the proxy exists to support could never work: every request failed at URL
 * construction, before any network call, and the client reported it as
 * `NetworkError: Unable to reach the server` — which sends you looking at the
 * service, or at your connection, for a bug in this line.
 *
 * `location.origin` supplies the base a relative path needs. It is read through
 * a guard rather than assumed, because this module also runs in React Native,
 * where there is no `location` and where the base is always configured
 * explicitly.
 */
function originBase(): string {
	if (typeof location !== "undefined" && location.origin) return location.origin
	return ""
}

function buildUrl(path: string, query?: RequestOptions["query"]): string {
	const joined = path.startsWith("http") ? path : `${baseUrl()}${path}`
	// A RELATIVE base is as valid as an empty one, and just as fatal to `new
	// URL`. `/api/v1` is the correct web development value — it goes through
	// vite's proxy, which is what keeps the app and the service on one origin so
	// the httpOnly session cookie behaves as it does in production. Both cases
	// are the same fix: supply the origin the path is missing.
	const url = new URL(joined.startsWith("http") ? joined : `${originBase()}${joined}`)
	if (query) {
		// Parameter names go over in the service's convention too: a filter sent
		// as `pageSize` is a filter the service does not apply, and an ignored
		// filter is a silently wrong list rather than an error.
		for (const [key, value] of Object.entries(toWireQuery(query))) {
			if (value === undefined || value === null) continue
			url.searchParams.set(key, String(value))
		}
	}
	return url.toString()
}

async function rawRequest(path: string, options: RequestOptions): Promise<Response> {
	const {
		method = "GET",
		body,
		query,
		headers = {},
		signal,
		timeoutMs = DEFAULT_TIMEOUT_MS,
		idempotencyKey,
		anonymous = false,
	} = options

	// Every request has a deadline. A hung request without one holds a connection
	// and a query slot indefinitely.
	const controller = new AbortController()
	const timeout = setTimeout(() => controller.abort(), timeoutMs)
	signal?.addEventListener("abort", () => controller.abort())

	// A multipart body is passed through untouched and, crucially, WITHOUT a
	// Content-Type: the boundary is generated by the runtime, and a hand-set
	// header produces a body the server cannot parse with no error to explain it.
	// Attachments are the only multipart path here — see ../files.
	let payload: BodyInit | undefined
	if (typeof FormData !== "undefined" && body instanceof FormData) {
		payload = body
	} else if (body !== undefined) {
		// Field names in the service's convention — see ./wire. A multipart body
		// is exempt: its part names (`title`, `attachments`, `notice`, `draft`)
		// are single words the service already fixed.
		payload = JSON.stringify(toWire(body))
	}

	const requestHeaders: Record<string, string> = {
		Accept: "application/json",
		...headers,
	}
	if (payload !== undefined && typeof payload === "string") {
		requestHeaders["Content-Type"] = "application/json"
	}
	if (!anonymous && accessToken) requestHeaders.Authorization = `Bearer ${accessToken}`
	if (idempotencyKey) requestHeaders["Idempotency-Key"] = idempotencyKey

	const finalUrl = buildUrl(path, query)
	const startedAt = Date.now()
	logger.debug("api.request", {
		method,
		url: finalUrl,
		hasBody: payload !== undefined,
		anonymous,
	})

	try {
		const response = await fetch(finalUrl, {
			method,
			headers: requestHeaders,
			...(payload !== undefined ? { body: payload } : {}),
			credentials: "include",
			signal: controller.signal,
		})
		logger.debug("api.response", {
			method,
			url: finalUrl,
			status: response.status,
			durationMs: Date.now() - startedAt,
		})

		// Bind the correlation ID from EVERY response, success or failure. This is
		// what lets a later client-side error report reference the server request
		// that caused it (OBSERVABILITY.md §2). Read through `correlationIdFrom`
		// so a success and a failure bind the same header.
		const correlationId = correlationIdFrom(response.headers)
		if (correlationId) {
			logger.setCorrelationId(correlationId)
			analytics.setCorrelationId(correlationId)
		}

		// The silent token exchange (US-00-001.16). Any authenticated response may
		// carry a renewed token; taking it here means every call site gets the
		// exchange for free and none of them has to know it happened.
		const renewed = response.headers.get(SESSION_TOKEN_HEADER)
		if (renewed && !anonymous) onSessionToken?.(renewed)

		// A 401 is the end of this session, not something to retry: the service
		// has no refresh route, and a token it refused once it will refuse again.
		// The token is dropped immediately so no later request on this tick reuses
		// it, but `onUnauthenticated` itself is NOT fired here — see `request()`,
		// which is where it actually runs, and why that is not the same tick.
		if (response.status === 401 && !anonymous) {
			setAccessToken(null)
		}

		return response
	} catch (error) {
		logger.debug("api.error", {
			method,
			url: finalUrl,
			durationMs: Date.now() - startedAt,
			aborted: controller.signal.aborted,
			message: error instanceof Error ? error.message : String(error),
		})
		if (controller.signal.aborted && !signal?.aborted) {
			throw new NetworkError(new Error(`Request to ${path} timed out after ${timeoutMs}ms`))
		}
		// A genuine transport failure — DNS, offline, CORS. Distinct from a 5xx,
		// because the server never saw the request.
		throw new NetworkError(error)
	} finally {
		clearTimeout(timeout)
	}
}

/**
 * Performs a request and PARSES THE RESPONSE WITH ITS SCHEMA.
 *
 * The schema is mandatory, not optional. Making it optional means the first
 * person in a hurry skips it, and the resulting `undefined is not an object`
 * surfaces three screens away with no clue where it came from.
 *
 *   const invoice = await request('/api/v1/invoices/123', invoiceSchema);
 *
 * GENERIC OVER THE SCHEMA, NOT OVER THE TYPE. `z.ZodType<T>` fixes the schema's
 * input type to its output type, which quietly rules out every schema that
 * transforms — and a response schema that has to accept two spellings of a field
 * the service changed its mind about is exactly such a schema. Inferring from
 * `z.output<S>` lets the wire shape and the shape the apps read differ where the
 * contract makes them differ, without the call site losing its type.
 */
export async function request<S extends z.ZodTypeAny>(
	path: string,
	schema: S,
	options: RequestOptions = {},
): Promise<z.output<S>> {
	type T = z.output<S>
	const response = await rawRequest(path, options)

	if (!response.ok) {
		throw await buildErrorAndNotify(response, options)
	}

	if (response.status === 204) {
		// A 204 has no body to parse. The caller's schema is `z.void()` or
		// `z.unknown()` on exactly the routes that answer this way.
		return undefined
	}

	let payload: unknown
	try {
		payload = await response.json()
	} catch (error) {
		throw new SchemaError(path, [
			{
				code: z.ZodIssueCode.custom,
				path: [],
				message: `Response was not valid JSON: ${String(error)}`,
			},
		])
	}

	// snake_case → camelCase BEFORE the schema sees it, so every schema in
	// ../domain is written once, in this package's convention (./wire).
	const parsed: z.SafeParseReturnType<unknown, T> = schema.safeParse(fromWire(payload))
	if (!parsed.success) {
		// A CONTRACT VIOLATION — the server sent something we do not understand,
		// usually a deploy skew or an undocumented API change. Surfaced loudly and
		// logged, because silently coercing it hides the real problem.
		logger.error("api response failed schema validation", {
			endpoint: path,
			issues: parsed.error.issues.map(i => `${i.path.join(".")}: ${i.message}`),
		})
		throw new SchemaError(path, parsed.error.issues)
	}

	return parsed.data
}

/**
 * Performs a request and returns the response BODY AS TEXT.
 *
 * The one endpoint in the product that does not answer JSON: the aggregate
 * export streams `text/csv` (US-01-006.13). It still goes through the same
 * transport — the same auth attachment, the same timeout, the same 401 handling,
 * the same correlation ID — because a second fetch path is a second place for
 * those to be forgotten.
 *
 * No schema, and none is possible: a CSV is not a shape Zod describes. Nothing
 * branches on its contents; it is handed straight to a download.
 */
export async function requestText(path: string, options: RequestOptions = {}): Promise<string> {
	const response = await rawRequest(path, options)
	if (!response.ok) throw await buildErrorAndNotify(response, options)
	return await response.text()
}

/**
 * Builds the `ApiError` for a failed response and, for a 401, fires
 * `onUnauthenticated` — deliberately AFTER the body has been read, not from
 * inside `rawRequest`.
 *
 * Firing it earlier (as this used to) raced the app's own sign-out sequence
 * against the very rejection it exists to react to: `ApiError.fromResponse`
 * below reads the response body, which is genuine stream I/O and can take
 * longer than the handler's in-memory bookkeeping (hold the destination, drop
 * the token, then `queryClient.clear()`). Whichever finished scheduling first
 * won the race — usually the handler, since draining a `ReadableStream` is
 * slower than a couple of promise hops — so `clear()` could destroy the very
 * Query still waiting on this body, orphaning its eventual rejection and
 * leaving the screen reading it on "Checking your session…" forever
 * (TC-US-00-001-9-3 and its siblings across the panel and reporting dashboard).
 *
 * Calling it here, once the error is fully built, means the throw that follows
 * is the very next thing that happens: the query's own rejection needs only a
 * few microtask hops to reach `notifyManager`, comfortably before the
 * handler's cache-clearing macrotask can run.
 */
async function buildErrorAndNotify(response: Response, options: RequestOptions): Promise<ApiError> {
	const error = await ApiError.fromResponse(response)
	if (response.status === 401 && !options.anonymous) onUnauthenticated?.()
	return error
}

// -----------------------------------------------------------------------------
// Convenience wrappers
// -----------------------------------------------------------------------------

export const api = {
	get: <S extends z.ZodTypeAny>(
		path: string,
		schema: S,
		options?: Omit<RequestOptions, "method" | "body">,
	) => request(path, schema, { ...options, method: "GET" }),

	post: <S extends z.ZodTypeAny>(
		path: string,
		schema: S,
		body?: unknown,
		options?: RequestOptions,
	) => request(path, schema, { ...options, method: "POST", body }),

	put: <S extends z.ZodTypeAny>(
		path: string,
		schema: S,
		body?: unknown,
		options?: RequestOptions,
	) => request(path, schema, { ...options, method: "PUT", body }),

	patch: <S extends z.ZodTypeAny>(
		path: string,
		schema: S,
		body?: unknown,
		options?: RequestOptions,
	) => request(path, schema, { ...options, method: "PATCH", body }),

	delete: <S extends z.ZodTypeAny>(path: string, schema: S, options?: RequestOptions) =>
		request(path, schema, { ...options, method: "DELETE" }),
}

/**
 * Wraps a request body in its schema before sending.
 *
 * Validating outbound as well as inbound catches a malformed payload at the call
 * site, where the stack trace points at the bug, rather than as a 422 that has to
 * be traced back from the network tab.
 */
export function validateBody<T>(schema: z.ZodType<T>, body: T): T {
	return schema.parse(body)
}

/** Generates an Idempotency-Key for a side-effecting request. */
export function newIdempotencyKey(): string {
	return typeof crypto !== "undefined" && "randomUUID" in crypto
		? crypto.randomUUID()
		: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}
